import { Timestamp } from "firebase/firestore";

import { Phase, ReminderType } from "../config/DiactiveTypes";
import { getBccEmails } from "../services/AppConfigService";
import { enablePatientAppAccess, enablePatientCanTrain, startPatientCountdown } from "../services/PatientService";
import { getUserEmail, getUserFullName } from "../services/UserService";
import { getAuthUserId } from "../services/AuthService";
import { sendTemplateEmail } from "../services/MailService";
import { reactivateReminder } from "../services/ReminderService";
import { calculateDaysRemaining, getPatientFullName, getPatientHspId } from "../utils/DiactiveUtils";
import { getHospitalName } from "../services/HospitalService";

export const sendAppAccessEmail = async (patient) => {
  const templateKey = "IntroAppAccessCtrl25";
  const bcc = await getBccEmails();
  await sendTemplateEmail(patient.email, bcc, templateKey);

  const hasAppAccess = await enablePatientAppAccess(patient.id);
  await enablePatientCanTrain(patient.id);
  return hasAppAccess;
};

export const sendResetCountdownDaysEmail = async (patient) => {
  const templateKey = "ActionResetCountdownDaysDoctor";
  const doctorEmail = await getUserEmail(patient.doctor);
  if (!doctorEmail || !patient.countdownStartedAt) return;

  const countdownStartedAt = Timestamp.fromDate(new Date(patient.countdownStartedAt));
  const daysRemaining = calculateDaysRemaining(countdownStartedAt, patient.isInStudy);
  const dynamicTemplateData = {
    patientHspId: getPatientHspId(patient),
    daysRemaining,
  };
  const bcc = await getBccEmails();
  await sendTemplateEmail(doctorEmail, bcc, templateKey, dynamicTemplateData);
};

export const sendReminderUploadFileEmail = async (patient, phase) => {
  const templateKey = "ReminderUploadFileDoctor";
  const doctorEmail = await getUserEmail(patient.doctor);
  const dynamicTemplateData = {
    hospitalName: getHospitalName(patient.hospital),
    patientName: getPatientFullName(patient),
    patientHspId: getPatientHspId(patient),
    mainUrl: `${window.location.origin}/patient/view/${patient.id}/files`
  };
  const bcc = await getBccEmails();
  await sendTemplateEmail(doctorEmail, bcc, templateKey, dynamicTemplateData);

  const reminderType = phase === Phase.PRE ? ReminderType.UploadGlucoseFilePre : ReminderType.UploadGlucoseFilePost;
  await reactivateReminder(patient.id, reminderType);
};

export const sendReminderFillQuestionnairesEmail = async (patient, isPostPhase) => {
  const templateKey = "ReminderFillQsPatient";
  const dynamicTemplateData = {
    patientFirstName: patient.firstName,
    mainUrl: window.location.origin
  };
  const bcc = await getBccEmails();
  await sendTemplateEmail(patient.email, bcc, templateKey, dynamicTemplateData);
};

export const sendDeletePatientEmail = async (patientHspId) => {
  if (!patientHspId) return;
  const templateKey = "ActionDeletePatientAdmin";
  const bcc = await getBccEmails();
  if (!bcc || bcc.length === 0) return;
  const adminFullName = await getUserFullName(getAuthUserId());
  await sendTemplateEmail(bcc[0], bcc.slice(1), templateKey, { patientHspId, adminFullName });
};

export const sendNewIncidentEmail = async (patientHspId) => {
  if (!patientHspId) return;
  const templateKey = "ActionNewIncidentAdmin";
  const bcc = await getBccEmails();
  if (!bcc || bcc.length === 0) return;
  await sendTemplateEmail(bcc[0], bcc.slice(1), templateKey, { patientHspId });
};

export const sendTrainingEmail = async (patient) => {
  let templateKey = "";
  const dynamicTemplateData = {};

  if (patient.canTrain) {
    templateKey = "ReminderTrainingIntr25";
    dynamicTemplateData.patientFirstName = patient.firstName;
  }
  else {
    templateKey = "IntroTrainingIntr25";
    await enablePatientCanTrain(patient.id);
    await startPatientCountdown(patient.id, patient.doctor);
  }

  const bcc = await getBccEmails();
  await sendTemplateEmail(patient.email, bcc, templateKey, dynamicTemplateData);
};